<template>
    <div class="card p-2 order-board" style="min-height: calc(100vh - 180px)">
        <div class="order-header">
            <TitleButton
                title="Sales Order (E-commerce)"
                btnTitle="Add New"
                :showBtn="false"
                :showAddNew="true"
                @onClickAddNewButton="navigateToAddPage"
            />
        </div>

        <div class="order-panel">
            <DateQuerySetter btnTitle="Go" @onClickGo="onClickGo">
                <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                    <label class="col-form-label">Customer</label>
                    <AsyncSelect
                        placeholder="Select Customer"
                        v-model="contactProfile"
                        :api-service="fetchContactProfiles"
                        :additional-query="additionalContactOption"
                    />
                </div>
                <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                    <label class="col-form-label">Order No</label>
                    <input
                        type="text"
                        class="form-control invoice-edit-input"
                        placeholder="Order No"
                        v-model="billNumber"
                    >
                </div>
            </DateQuerySetter>

            <div v-if="hasTags" class="tag-filter d-flex flex-wrap align-items-center gap-2">
                <span class="tag-filter-label text-muted small fw-semibold"><i class="fas fa-filter me-1"></i>Filter by tag</span>
                <span
                    class="badge tag-pill cursor-pointer"
                    :class="!selectedTagId ? 'bg-primary' : 'bg-light text-dark border'"
                    @click="onSelectTag(null)"
                >All</span>
                <span
                    v-for="tag in tagList"
                    :key="tag.id"
                    class="badge tag-pill cursor-pointer"
                    :class="isTagActive(tag.id) ? 'bg-primary' : 'bg-light text-dark border'"
                    @click="onSelectTag(tag.id)"
                ><i class="fas fa-tag me-1"></i>{{ tag.name }}</span>
            </div>
        </div>

        <div class="order-tabs">
            <button
                v-for="column in columns"
                :key="column.value"
                type="button"
                class="order-tab"
                :class="{ active: activeTab === column.value }"
                @click="onChangeTab(column.value)"
            >
                <span class="order-tab-label">{{ column.name }}</span>
                <span class="order-tab-count">{{ column.count }}</span>
            </button>
        </div>

        <Loader v-if="loading" />

        <div v-else class="table-responsive order-table mt-2">
            <table class="table table-bordered table-hover">
                <thead>
                    <tr>
                        <th>Customer Name</th>
                        <th>Order No</th>
                        <th>Order Date</th>
                        <th class="text-end">Order Amount</th>
                        <th v-if="showInvoiceColumns">Invoice No</th>
                        <th v-if="showInvoiceColumns">Invoice Date</th>
                        <th v-if="showInvoiceColumns" class="text-end">Invoice Amount</th>
                        <th class="text-center">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="order in orders" :key="order.id">
                        <td>{{ customerLabel(order) }}</td>
                        <td>{{ order.bill_number || order.order_number }}</td>
                        <td>{{ order.order_date }}</td>
                        <td class="text-end">{{ order.total_amount }}</td>
                        <td v-if="showInvoiceColumns">{{ order.invoice_number }}</td>
                        <td v-if="showInvoiceColumns">{{ order.invoice_date }}</td>
                        <td v-if="showInvoiceColumns" class="text-end">{{ order.invoice_amount }}</td>
                        <td class="text-center">
                            <div class="btn-group">
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary dropdown-toggle"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                    title="Actions"
                                >
                                    <i class="fa-solid fa-ellipsis-vertical"></i>
                                </button>
                                <div class="dropdown-menu dropdown-menu-end">
                                    <a
                                        v-if="isEditable"
                                        @click="goToEdit(order)"
                                        class="dropdown-item"
                                        href="javascript:void(0)"
                                    ><i class="fas fa-pencil-alt me-1"></i> Edit</a>
                                    <a
                                        @click="openActionModal(order)"
                                        class="dropdown-item"
                                        href="javascript:void(0)"
                                    ><i class="fas fa-pen-to-square me-1"></i> {{ statusActionLabel }}</a>
                                    <a
                                        v-if="activeTab === 'ready_to_ship'"
                                        @click="printOrder(order)"
                                        class="dropdown-item"
                                        href="javascript:void(0)"
                                    ><i class="fas fa-print me-1"></i> Print Order</a>
                                    <a
                                        v-if="activeTab === 'stock_in_transit'"
                                        @click="openMakeDeliveryModal(order)"
                                        class="dropdown-item"
                                        href="javascript:void(0)"
                                    ><i class="fas fa-truck me-1"></i> Make Delivery</a>
                                    <a
                                        v-if="activeTab === 'stock_in_transit'"
                                        @click="openDeliveryCancelModal(order)"
                                        class="dropdown-item text-danger"
                                        href="javascript:void(0)"
                                    ><i class="fas fa-ban me-1"></i> Delivery Cancelled</a>
                                    <a
                                        v-if="showInvoiceColumns && order.sale_master_id"
                                        @click="goToPrint(order.sale_master_id, 1)"
                                        class="dropdown-item"
                                        href="javascript:void(0)"
                                    ><i class="fas fa-print me-1"></i> Print Invoice</a>
                                    <a
                                        v-if="showInvoiceColumns && order.sale_master_id"
                                        @click="goToPrint(order.sale_master_id, 2)"
                                        class="dropdown-item"
                                        href="javascript:void(0)"
                                    ><i class="fas fa-print me-1"></i> Print Delivery Challan</a>
                                    <a
                                        v-if="showInvoiceColumns && order.sale_master_id && order.invoice_status !== 'returned'"
                                        @click="goToReturnPage(order.sale_master_id)"
                                        class="dropdown-item"
                                        href="javascript:void(0)"
                                    ><i class="far fa-window-close me-1"></i> Return Invoice</a>
                                    <a
                                        v-if="showPartialReturnColumns && order.sale_master_id && shouldShowPartialReturn(order)"
                                        @click="goToPartialReturn(order.sale_master_id)"
                                        class="dropdown-item"
                                        href="javascript:void(0)"
                                    ><i class="fas fa-undo-alt me-1"></i> Partial Return</a>
                                    <a
                                        @click="openTagModal(order)"
                                        class="dropdown-item"
                                        href="javascript:void(0)"
                                    ><i class="fas fa-tags me-1"></i> Tags</a>
                                </div>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="!hasOrders">
                        <td :colspan="emptyColspan" class="text-center text-muted">No orders found.</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="hasOrders" class="d-flex px-2 mt-2">
            <Pagination
                :offset="offset"
                @onPageChange="onPageChange"
            />
        </div>

        <OrderActionModal
            v-if="selectedOrder"
            :order="selectedOrder"
            :workflow-statuses="workflowStatuses"
            :workflow-transitions="workflowTransitions"
            :forced-status="forcedStatus"
            @close="onCloseModal"
            @updated="onActionDone"
        />

        <OrderTagModal
            v-if="tagOrder"
            :order="tagOrder"
            @close="onCloseTagModal"
            @updated="onTagDone"
        />

        <MakeDeliveryModal
            v-if="deliveryOrder"
            :order="deliveryOrder"
            @close="onCloseDeliveryModal"
            @created="onDeliveryDone"
        />
    </div>
</template>

<script setup>
import { ref, onMounted, computed, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TitleButton from '@/components/atom/TitleButton'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import OrderActionModal from './OrderActionModal.vue'
import OrderTagModal from './OrderTagModal.vue'
import MakeDeliveryModal from './MakeDeliveryModal.vue'
import handlePurchase from '@/services/modules/purchase'
import handleContact from '@/services/modules/contact'
import handleInventory from '@/services/modules/inventory'
import handleCompany from '@/services/modules/company'
import handleBarcode from '@/services/modules/barcode'
import handleHospital from '@/services/modules/hospital'
import pdfPrinter from '@/services/utils/pdfPrinter'
import { formatDate } from '@/services/utils/global'
import { useStore } from 'vuex'

const route = useRoute()
const router = useRouter()
const store = useStore()

const showError = inject('showError')

const { getEcommerceOrderStatusColumns, getEcommerceOrders, fetchSalesOrderById, fetchSalesBill } = handlePurchase()
const { fetchContactProfiles } = handleContact()
const { fetchHome } = handleInventory()
const { fetchCompanyInfo, fetchActiveCustomizationField } = handleCompany()
const { fetchBarcode } = handleBarcode()
const { notesByType } = handleHospital()
const { exportToPDF } = pdfPrinter()

const loading = ref(false)
const offset = ref(20)
const companyInfo = ref({})
const invoiceNotes = ref([])
const customizationPdfFields = ref([])
const columns = ref([])
const orders = ref([])
const workflowStatuses = ref([])
const workflowTransitions = ref({})
const activeTab = ref(route.query.tab || 'pending')
const selectedOrder = ref(null)
const forcedStatus = ref(null)
const deliveryOrder = ref(null)
const tagOrder = ref(null)
const tagList = ref([])
const selectedTagId = ref(null)
const contactProfile = ref(null)
const billNumber = ref(null)
const additionalContactOption = ref({
    with_mobile: 1,
    roles: 'customer,lead'
})

const companyId = computed(() => route.params.companyId)

const hasOrders = computed(() => orders.value.length > 0)

const showInvoiceColumns = computed(() => activeTab.value === 'delivered' || activeTab.value === 'delivered_cancelled')
const showPartialReturnColumns = computed(() => activeTab.value === 'delivered' || activeTab.value === 'delivered_cancelled' || activeTab.value === 'sales_return')

const isEditable = computed(() => activeTab.value === 'pending' || activeTab.value === 'on_hold')

const statusActionLabel = computed(() => {
    const viewOnlyTabs = ['stock_in_transit', 'delivered', 'sales_return', 'cancelled', 'delivered_cancelled']
    if (viewOnlyTabs.includes(activeTab.value)) return 'View Status'
    return 'Update Status'
})

const emptyColspan = computed(() => {
    if (showInvoiceColumns.value) return 8
    return 5
})

const hasTags = computed(() => tagList.value.length > 0)

const baseQuery = () => {
    let query = `?company_id=${companyId.value}`
    query += `&start_date=${route.query.start || ''}`
    query += `&end_date=${route.query.end || ''}`
    if (contactProfile.value) query += `&contact_profile_id=${contactProfile.value.id}`
    if (billNumber.value) query += `&bill_number=${billNumber.value}`
    return query
}

const customerLabel = (order) => {
    if (!order.party_name) return ''
    if (!order.serial_no) return order.party_name
    return `${order.party_name} (${order.serial_no})`
}

const getWorkflowStatuses = async () => {
    if (workflowStatuses.value.length > 0) return
    const res = await fetchHome()
    if (!res.data || !res.data.order_workflow_status) return
    workflowStatuses.value = res.data.order_workflow_status
    if (res.data.order_workflow_transitions) {
        workflowTransitions.value = res.data.order_workflow_transitions
    }
}

const getColumns = async () => {
    const res = await getEcommerceOrderStatusColumns(baseQuery())
    if (!res.status) {
        columns.value = []
        return
    }
    columns.value = res.data
}

const getTabOrders = async () => {
    loading.value = true
    let query = `${baseQuery()}&offset=${offset.value}&page=${route.query.page || 1}&order_status=${activeTab.value}`
    if (selectedTagId.value) {
        query += `&tag_id=${selectedTagId.value}`
    }

    const res = await getEcommerceOrders(query)
    loading.value = false
    if (!res.status) {
        orders.value = []
        store.commit('resetPagination')
        return
    }
    orders.value = res.data.data
    setPagination(res.data)
}

const setPagination = (data) => {
    if (!data.data.length) return
    store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page
    })
}

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({ path: route.path, query: routeQuery })
    await getTabOrders()
}

const loadAll = async () => {
    await getWorkflowStatuses()
    await getColumns()
    await getTabOrders()
}

const onClickGo = () => {
    loadAll()
}

const resetPage = () => {
    if (!route.query.page) return
    let routeQuery = Object.assign({}, route.query)
    delete routeQuery.page
    router.replace({ path: route.path, query: routeQuery })
}

const onChangeTab = (status) => {
    activeTab.value = status
    selectedTagId.value = null
    let routeQuery = Object.assign({}, route.query)
    routeQuery.tab = status
    delete routeQuery.page
    router.replace({ path: route.path, query: routeQuery })
    getTabOrders()
}

const getTagList = async () => {
    const res = await notesByType(`?company_id=${companyId.value}`, 'order_tag')
    if (!res.status || !res.data) return
    tagList.value = res.data
}

const isTagActive = (tagId) => {
    if (!selectedTagId.value) return false
    return String(selectedTagId.value) === String(tagId)
}

const onSelectTag = (tagId) => {
    resetPage()
    if (isTagActive(tagId)) {
        selectedTagId.value = null
        getTabOrders()
        return
    }
    selectedTagId.value = tagId
    getTabOrders()
}

const openActionModal = (order) => {
    forcedStatus.value = null
    selectedOrder.value = order
}

const openDeliveryCancelModal = (order) => {
    forcedStatus.value = 'delivered_cancelled'
    selectedOrder.value = order
}

const openMakeDeliveryModal = (order) => {
    deliveryOrder.value = order
}

const onCloseDeliveryModal = () => {
    deliveryOrder.value = null
}

const onDeliveryDone = () => {
    deliveryOrder.value = null
    loadAll()
}

const onCloseModal = () => {
    selectedOrder.value = null
    forcedStatus.value = null
}

const onActionDone = () => {
    selectedOrder.value = null
    forcedStatus.value = null
    loadAll()
}

const openTagModal = (order) => {
    tagOrder.value = order
}

const onCloseTagModal = () => {
    tagOrder.value = null
}

const onTagDone = () => {
    loadAll()
}

const navigateToAddPage = () => {
    router.push({ name: 'sales-order-ecommerce-add', params: route.params, query: route.query })
}

const goToEdit = (order) => {
    router.push({
        name: 'sales-order-ecommerce-edit',
        params: { ...route.params, salesId: order.id },
        query: route.query
    })
}

const getCompanyInfo = async () => {
    const res = await fetchCompanyInfo(companyId.value)
    if (!res.data) return
    companyInfo.value = res.data
}

const getInvoiceNotes = async () => {
    const companyQuery = `?company_id=${companyId.value}&show_default_only=1`
    const res = await notesByType(companyQuery, 'invoice_terms')
    if (!res.status) return
    invoiceNotes.value = res.data.filter(item => item.full_desc)
}

const getActiveFieldCustomization = async () => {
    const companyQuery = `?company_id=${companyId.value}&customization_type=pdf&type=sale_invoice_pdf_list`
    const res = await fetchActiveCustomizationField(companyQuery)
    if (!res.data) return
    customizationPdfFields.value = res.data
}

const printOrder = async (order) => {
    const companyQuery = `?company_id=${companyId.value}`
    const res = await fetchSalesOrderById(order.id, companyQuery)
    if (!res.data) {
        return showError('Order information not found.')
    }
    const singleSalesOrder = res.data
    const qrcodeData = JSON.stringify({
        ref_no: singleSalesOrder.bill_number,
        ref_date: singleSalesOrder.date,
        created_by: singleSalesOrder.created_by_name,
        created_date_time: formatDate(new Date())
    })
    const barcodeQuery = `?code=${singleSalesOrder.bill_number}&qrcode=${qrcodeData}`
    const barcodeRes = await fetchBarcode(barcodeQuery)
    if (!barcodeRes.status) return
    const user = store.state.user.user
    exportToPDF(companyInfo.value, { ...singleSalesOrder, is_order: true }, barcodeRes.barcode, barcodeRes.qrcode, user.name, 1, invoiceNotes.value, true, customizationPdfFields.value)
}

const shouldShowPartialReturn = (order) => {
    return order.invoice_status !== 'returned'
}

const goToReturnPage = (saleMasterId) => {
    router.push({
        name: 'general-bill-return',
        params: { ...route.params, invoiceId: saleMasterId },
        query: { ...route.query, redirect_route: route.name, order_source: 'sales_order_ecommerce' }
    })
}

const goToPartialReturn = (saleMasterId) => {
    router.push({
        name: 'general-bill-partial-return',
        params: { ...route.params, invoiceId: saleMasterId },
        query: { ...route.query, redirect_route: route.name, order_source: 'sales_order_ecommerce' }
    })
}

const goToPrint = async (saleMasterId, btnFunc) => {
    const companyQuery = `?company_id=${companyId.value}`
    const res = await fetchSalesBill(saleMasterId, companyQuery)
    if (!res.status) return
    const invoice = res.data
    const qrcodeData = JSON.stringify({
        ref_no: invoice.bill_number,
        ref_date: invoice.date,
        created_by: invoice.created_by_name,
        created_date_time: formatDate(new Date())
    })
    const barcodeQuery = `?code=${invoice.bill_number}&qrcode=${qrcodeData}`
    const barcodeRes = await fetchBarcode(barcodeQuery)
    if (!barcodeRes.status) return
    const user = store.state.user.user
    exportToPDF(companyInfo.value, invoice, barcodeRes.barcode, barcodeRes.qrcode, user.name, btnFunc, invoiceNotes.value, true, customizationPdfFields.value)
}

onMounted(() => {
    loadAll()
    getTagList()
    getCompanyInfo()
    getInvoiceNotes()
    getActiveFieldCustomization()
})
</script>

<style scoped>
.order-header {
    padding-bottom: 12px;
    margin-bottom: 14px;
    border-bottom: 1px solid #ebe9f1;
}
.order-panel {
    background: #f8f8fb;
    border: 1px solid #ebe9f1;
    border-radius: 8px;
    padding: 14px 14px 6px;
    margin-bottom: 16px;
}
.tag-filter {
    margin-top: 10px;
    padding-top: 12px;
    border-top: 1px dashed #d8d6e3;
}
.tag-filter-label {
    margin-right: 4px;
}
.order-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    border-bottom: 2px solid #ebe9f1;
}
.order-tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    margin-bottom: -2px;
    border: none;
    border-bottom: 2px solid transparent;
    background: transparent;
    color: #6e6b7b;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: color 0.15s ease, border-color 0.15s ease;
}
.order-tab:hover {
    color: #7367f0;
}
.order-tab.active {
    color: #7367f0;
    border-bottom-color: #7367f0;
}
.order-tab-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 17px;
    padding: 0 5px;
    border-radius: 999px;
    background: #e7e6f7;
    color: #7367f0;
    font-size: 10px;
    font-weight: 700;
}
.order-tab.active .order-tab-count {
    background: #7367f0;
    color: #fff;
}
.order-table {
    overflow: visible;
}
.order-table :deep(table) {
    margin-bottom: 0;
}
.order-table :deep(thead th) {
    background: #f3f2f7;
    color: #5e5873;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
}
.order-table :deep(tbody td) {
    vertical-align: middle;
    font-size: 13px;
}
.tag-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 14px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.2px;
    transition: all 0.15s ease;
}
.tag-pill:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}
.tag-pill.bg-light:hover {
    border-color: #7367f0 !important;
    color: #7367f0 !important;
}
</style>

